import React, { useCallback, useEffect, useState } from 'react';
import { v4 as uuidV4 } from 'uuid';
import { useForm, FormProvider } from 'react-hook-form';
import { useTable, useTimeWindow } from './hooks';
import { BenchmarkForm } from '../../components/BenchmarkMetadata/components/BenchmarkForm/BenchmarkForm';
import { CompositeWeightedTimeWindow } from './CompositeWeightedTimeWindow';
import { useYupValidationResolver } from '../../helpers/form';
import {
  BenchmarkScreen,
  useBenchmarkDetails,
  useTWNavigation,
  useTWScreenParams,
  useTimeWindows,
} from 'hooks';
import { useSubmit } from 'hooks/useSubmit';
import { getBody } from './CompositeWeightedHelpers';
import { showSuccess } from 'helpers/showSuccess';
import { DevTool } from '@hookform/devtools';
import {
  CompositeWeighted,
  CompositeWeightedLineItem,
  CompositeWeightedTimeWindow as CompositeWeightedTimeWindowType,
} from 'types/CompositeWeighted';
import {
  compositeWeightedValidationSchema,
  compositeWeightedTimeWindowShape,
} from './CompositeWeightedValidationScheme';
import { BenchmarkMetadata } from 'components/BenchmarkMetadata';
import { DefaultDisplay } from './components/DefaultDisplay';
import { SmallDisplay } from './components/SmallDisplay';
import { CompositeWeightedTimeWindowHeader } from './components/TimeWindowHeader';
import { Footer, TableTabbed } from 'components';
import { getToDates } from 'helpers/timeWindow/getToDates';
import { BenchmarkTypes } from 'types/Benchmark';
import { BenchmarkFormTitle } from 'components/BenchmarkMetadata/components/BenchmarkForm/BenchmarkFormStyles';
import { TimeWindowsEditTitle, TimeWindows } from 'components/TimeWindow';
import { useProblemsHandler } from 'modules/BenchmarkForm/hooks/useProblemsHandler';
import { Api } from 'api';
import {
  TagDefinition,
  TagRefDefinitionDataProps,
} from 'types/TagRefDefinition';
import { LoadingWrapper } from 'components/LoadingWrapper/LoadingWrapper';

type Props = {};

export const BenchmarkCompositeWeighted: React.FC<Props> = () => {
  const [isFormLoadComplete, setIsFormLoadComplete] = useState(false);
  const { data: tagRefOptions, isLoading: isTagRefsLoading } =
    Api.lookup.GetTagRefPropertyDefinitions();

  const resolver = useYupValidationResolver(
    compositeWeightedValidationSchema()
  );

  const methods = useForm<any>({
    resolver,
    defaultValues: {
      baseCurrencyCode: 'USD',
      lookThroughDepth: 1,
      pushTimeWindowsToPortfolioAnalytics: 'false',
    },
  });

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
    setValue,
    getValues,
    setError,
  } = methods;

  const {
    benchmark,
    refetch,
    isLoading: isLoadingBenchmark,
  } = useBenchmarkDetails<CompositeWeighted>();

  const { isTimeWindowsScreen, addTimeWindow, benchmarkScreen } =
    useTWScreenParams(benchmark?.id);

  const handleTWNavigation = useTWNavigation(benchmarkScreen);

  const { timeWindows, timeWindowsFilled } =
    useTimeWindows<CompositeWeightedTimeWindowType>({
      setValue,
      control,
      timeWindowSchema: compositeWeightedTimeWindowShape,
    });

  const tableProps = useTable(timeWindows);

  const {
    onSubmit,
    isSuccess,
    isLoading: isSubmitting,
    errors: problemDetails,
  } = useSubmit({
    originalValues: benchmark,
    getBody,
    originalTimeWindows: timeWindows,
  });

  useProblemsHandler(setError, problemDetails);

  useEffect(() => {
    if (isSuccess) {
      if (benchmark?.id) {
        const values = getValues();
        reset(values);
        showSuccess('Benchmark was successfully saved');
        refetch();
        handleTWNavigation();
        window.scrollTo(0, 0);
      }
    }
  }, [isSuccess, benchmark?.id]);

  const resetData = useCallback(() => {
    reset();
    if (benchmark && tagRefOptions) {
      let values: any = {
        ...benchmark,
        timeWindows: getToDates(
          benchmark?.timeWindows?.map(
            (item: CompositeWeightedTimeWindowType) => {
              const propDef =
                tagRefOptions?.find(
                  (tag: TagRefDefinitionDataProps) =>
                    +tag.id === +item.tagRefPropertyDefinitionId
                ) ?? [];
              const tags = propDef.flattenedTags;

              const {
                fromDate,
                assetSelectionSetId,
                tagRefPropertyDefinitionId,
                lineItems,
              } = item;
              return {
                toggleId: uuidV4(),
                fromDate: new Date(fromDate),
                assetSelectionSetId,
                tagRefPropertyDefinitionId,
                tagIds: lineItems
                  ?.map(
                    (lineItem: CompositeWeightedLineItem) =>
                      lineItem.weightingComponentTagId
                  )
                  .join(','),
                lineItems: lineItems.map((lineItem) => {
                  return {
                    returnComponentResourceId:
                      lineItem.returnComponentBenchmarkId ||
                      lineItem.returnComponentResourceId,
                    returnComponentName: lineItem.returnComponentName,
                    weightingComponentTagId: lineItem.weightingComponentTagId,
                    isLagged: lineItem.isLagged,
                    customLagDate: lineItem.customLagDate,
                    defaultLagDate: benchmark.defaultLagDate,
                    weightingComponentName:
                      tags.find(
                        (tag: TagDefinition) =>
                          tag.id === lineItem.weightingComponentTagId
                      )?.name ?? '',
                  };
                }),
                loadingWeightingComponents: false,
              };
            }
          )
        ),
      };

      reset(values);
    }
  }, [benchmark, reset, tagRefOptions]);

  useEffect(() => {
    resetData();
  }, [resetData]);

  const tableTabbedProps = {
    ...tableProps,
    useTimeWindow,
    handleSubmit,
    onSubmit,
    isLoading: isSubmitting || isLoadingBenchmark || isTagRefsLoading,
    benchmarkName: benchmark?.name,
    benchmarkId: benchmark?.id,
  };

  const renderContent = () => {
    if (isLoadingBenchmark) return null;

    if (isTimeWindowsScreen || addTimeWindow) {
      return (
        <TimeWindows
          benchmarkType={BenchmarkTypes.CompositeWeighted}
          timeWindowsFilled={timeWindowsFilled}
          benchmarkId={benchmark?.id}
          HeaderComponent={CompositeWeightedTimeWindowHeader}
          TimeWindowComponent={CompositeWeightedTimeWindow}
          defaultValues={{
            fromDate: new Date(),
            toggleId: uuidV4(),
            assetSelectionSetId: '',
            tagRefPropertyDefinitionId: '',
            tagIds: '',
            lineItems: [],
          }}
        />
      );
    }

    return <TableTabbed {...tableTabbedProps} />;
  };

  return (
    <LoadingWrapper isLoading={isLoadingBenchmark && isFormLoadComplete}>
      <TimeWindowsEditTitle isTimeWindowsScreen={isTimeWindowsScreen} />
      {benchmark?.id && !isSubmitting ? (
        <BenchmarkMetadata
          benchmark={benchmark}
          onConfirm={refetch}
          isTimeWindowsScreen={isTimeWindowsScreen}
        >
          <SmallDisplay benchmark={benchmark} />
          <DefaultDisplay benchmark={benchmark} />
        </BenchmarkMetadata>
      ) : null}
      <FormProvider {...methods}>
        {benchmark === undefined ? (
          <div style={{ padding: 20 }}>
            <BenchmarkFormTitle>
              Add New Composite Weighted Benchmark
            </BenchmarkFormTitle>
            <BenchmarkForm
              benchmarkType={BenchmarkTypes.CompositeWeighted}
              benchmark={benchmark}
              onLoadComplete={() => setIsFormLoadComplete(true)}
            />
          </div>
        ) : null}
        {renderContent()}
        <Footer
          showReturnHome={benchmarkScreen === BenchmarkScreen.editBenchmark}
          onSave={handleSubmit((data: any) => {
            if (!data.pushTimeWindowsToPortfolioAnalytics) {
              delete data['lookThroughDepth'];
            }

            onSubmit({
              ...data,
              onSaveSuccess: isSuccess,
            });
          })}
          onDiscardChanges={() => {
            resetData();
            handleTWNavigation();
            window.scrollTo(0, 0);
          }}
          isSaveDisabled={!isDirty || errors.timeWindows}
          isLoading={tableTabbedProps.isLoading}
          showEditButtons={Boolean(isTimeWindowsScreen || !benchmark?.id)}
          discardText='Cancel'
          isDirty={isDirty}
        />
        <DevTool control={control} placement='top-left' />
      </FormProvider>
    </LoadingWrapper>
  );
};
